import React from 'react'
import { DEFAULT_FILTERS } from '../constants'
import {
  AdminSubscriptionFilters,
  AdminSubscriptionSortKey,
  AdminSubscriptionTableRow,
  buildCustomerTransactionSearchIndex,
  filterAdminSubscriptionRows,
  sortAdminSubscriptionRows,
} from '../utils'

export const useSubscriptionsTableState = (
  rows: AdminSubscriptionTableRow[]
) => {
  const [filters, setFilters] = React.useState(DEFAULT_FILTERS)
  const [sortKey, setSortKey] = React.useState<AdminSubscriptionSortKey>(
    'latestTransactionStatus'
  )
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>(
    'desc'
  )
  const [expandedRows, setExpandedRows] = React.useState<
    Record<string, boolean>
  >({})

  const statusOptions = React.useMemo(
    () => [
      'all',
      ...new Set(
        rows
          .map((row) => row.status)
          .sort((left, right) => left.localeCompare(right))
      ),
    ],
    [rows]
  )

  const customerTransactionSearchIndex = React.useMemo(
    () => buildCustomerTransactionSearchIndex(rows),
    [rows]
  )

  const filteredRows = React.useMemo(
    () =>
      filterAdminSubscriptionRows(
        rows,
        filters,
        customerTransactionSearchIndex
      ),
    [rows, filters, customerTransactionSearchIndex]
  )

  const visibleRows = React.useMemo(
    () => sortAdminSubscriptionRows(filteredRows, sortKey, sortDirection),
    [filteredRows, sortKey, sortDirection]
  )

  const summary = React.useMemo(
    () => ({
      customers: new Set(rows.map((row) => row.customerProfileId)).size,
      subscriptions: rows.length,
      transactions: rows.reduce(
        (accumulator, row) => accumulator + row.transactionCount,
        0
      ),
      visible: visibleRows.length,
    }),
    [rows, visibleRows.length]
  )

  const onSort = React.useCallback(
    (key: AdminSubscriptionSortKey) => {
      if (sortKey === key) {
        setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'))
        return
      }

      setSortKey(key)
      setSortDirection(
        key === 'customerName' || key === 'unitLabel' ? 'asc' : 'desc'
      )
    },
    [sortKey]
  )

  const onToggleRow = React.useCallback((subscriptionId: string) => {
    setExpandedRows((current) => ({
      ...current,
      [subscriptionId]: !current[subscriptionId],
    }))
  }, [])

  const onChangeFilter = React.useCallback(
    <T extends keyof AdminSubscriptionFilters>(
      key: T,
      value: AdminSubscriptionFilters[T]
    ) => {
      setFilters((current) => ({
        ...current,
        [key]: value,
      }))
    },
    []
  )

  return {
    expandedRows,
    filters,
    onChangeFilter,
    onSort,
    onToggleRow,
    sortDirection,
    sortKey,
    statusOptions,
    summary,
    visibleRows,
  }
}
